// Thin fetch wrapper for /api/admin/users endpoints.
//
// Schema parity with SCIM POST /scim/v2/Users — both paths write the same
// IdentityItem rows. Audit attribution differs by the row's `source` field
// ("admin" for these calls, "scim" for SCIM ingest).

import { adminGet, adminPost } from "@/data/httpClient";
import { unwrapEnvelope } from "@/data/unwrapEnvelope";
import { request, ApiError } from "@/api";

export interface AdminUser {
  alias: string;
  tenant_id: string;
  ring_user_id: string | null;
  amazon_customer_id: string | null;
  provisioned_at: string;
  updated_at: string;
}

export interface AdminUserListEntry {
  alias: string;
  ring_user_id: string | null;
}

export interface UserClient {
  list(): Promise<{ items: AdminUserListEntry[] }>;
  get(alias: string): Promise<AdminUser>;
  create(req: {
    alias: string;
    ringUserId?: string;
    amazonCustomerId?: string;
  }): Promise<AdminUser>;
  update(
    alias: string,
    patch: { ringUserId?: string; amazonCustomerId?: string },
  ): Promise<AdminUser>;
  delete(alias: string): Promise<{ deleted: true; memberships_removed: number }>;
}

const BASE = "/api/admin/users";

async function adminPatchPlain<T>(path: string, body: unknown): Promise<T> {
  const res = await request<T>(path, { method: "PATCH", body });
  if (res === null) {
    throw new ApiError(0, "aborted", "request aborted", undefined, null);
  }
  return res.data;
}

async function adminDeletePlain<T>(path: string): Promise<T> {
  const res = await request<T>(path, { method: "DELETE" });
  if (res === null) {
    throw new ApiError(0, "aborted", "request aborted", undefined, null);
  }
  return res.data;
}

function createUserClient(): UserClient {
  return {
    list() {
      return adminGet<{ users?: AdminUserListEntry[]; items?: AdminUserListEntry[] }>(
        BASE,
      ).then((resp) => ({ items: resp.users ?? resp.items ?? [] }));
    },
    get(alias) {
      // Enveloped as {"user": {...}}; unwrap to a bare AdminUser.
      return adminGet<AdminUser>(`${BASE}/${encodeURIComponent(alias)}`).then(
        (resp) => unwrapEnvelope(resp, "user"),
      );
    },
    create(req) {
      // Wire fields are snake_case (Smithy CreateUserRequest binds
      // ring_user_id / amazon_customer_id). Map from the camelCase JS args.
      return adminPost<AdminUser>(BASE, {
        alias: req.alias,
        ring_user_id: req.ringUserId,
        amazon_customer_id: req.amazonCustomerId,
      }).then((resp) => unwrapEnvelope(resp, "user"));
    },
    update(alias, patch) {
      // No If-Match: the underlying IdentityItem has no version — DDB
      // unconditional put is the same shape SCIM uses. The handler returns
      // 404 if the row is missing. Wire fields are snake_case.
      return adminPatchPlain<AdminUser>(
        `${BASE}/${encodeURIComponent(alias)}`,
        {
          ring_user_id: patch.ringUserId,
          amazon_customer_id: patch.amazonCustomerId,
        },
      ).then((resp) => unwrapEnvelope(resp, "user"));
    },
    delete(alias) {
      return adminDeletePlain<{ deleted: true; memberships_removed: number }>(
        `${BASE}/${encodeURIComponent(alias)}`,
      );
    },
  };
}

export const userClient: UserClient = createUserClient();
